import React from "react";
import { format } from "date-fns";
import { Button, Icon, Image } from "semantic-ui-react";
import { ensureHttpsImageUrl } from "../../../../app/common/util/util";
import { REPORT_STATUS_LABELS, failedSteps } from "./piModel";
import { PI_REPORT_CSS } from "./piStyles";
import { HistoryPermitsTab, HoaMetroTab, NeighborhoodTab, OverviewTab, PropertyDetailsTab, RisksInsuranceTab } from "./PiTabs";
import PiFullReport from "./PiFullReport";
import PiClientSummaryEditor from "./PiClientSummaryEditor";

export const TABS = [
  ["overview", "Overview", "tachometer alternate"],
  ["details", "Property Details", "home"],
  ["history", "History & Permits", "history"],
  ["hoaMetro", "HOA & Metro", "building outline"],
  ["risks", "Risks & Insurance", "shield alternate"],
  ["neighborhood", "Neighborhood", "map outline"],
  ["fullReport", "Full Report", "book"],
  ["clientSummary", "Client Summary", "user outline"],
];

function toDate(value) {
  if (!value) return null;
  const d = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The completed report: a fixed header (property, status, actions, tabs)
 * above a single scrolling body. Purely presentational - the modal owns
 * loading, editing and sharing - and it renders only the saved report and
 * its server-built presentation `presentation`.
 */
export default function PiReportView({
  transaction, report, presentation: p, modules, tab, onTab, isNarrow, bodyRef, resuming, clientParties,
  onClose, onResume, onEdit, onSaveSummary, onShare,
}) {
  const address = report?.propertyAddress || transaction.address || {};
  const completedAt = toDate(report?.researchCompletedAt);
  const statusTone = report.status === "completed" ? "positive" : "caution";

  return (
    <>
      <style>{PI_REPORT_CSS}</style>
      <div className="pi-report">
        <header className="pi-head">
          <div className="pi-brand">
            <Image src="/assets/TA-Logo-Pro.png" alt="TransActioner Pro" />
            <span className="pi-eyebrow">Property Intelligence</span>
            <button type="button" className="pi-close" onClick={onClose} aria-label="Close Property Intelligence">
              <Icon name="close" />
            </button>
          </div>
          <div className="pi-prop">
            <Image className="pi-thumb" src={transaction.pic ? ensureHttpsImageUrl(transaction.pic) : "/assets/placeholder-house.png"} alt="" />
            <div className="pi-address">
              <h2>{address.street}{address.unit ? ` ${address.unit}` : ""}</h2>
              <div className="pi-city">{[address.city, [address.state, address.zipcode].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div>
              <div className="pi-meta">
                <span className={`pi-badge ${statusTone}`}>{p?.header?.statusLabel || REPORT_STATUS_LABELS[report.status]}</span>
                {completedAt && <span className="pi-date">Researched {format(completedAt, "MMM d, yyyy")}</span>}
              </div>
            </div>
            <div className="pi-actions">
              <Button basic className="pi-secondary-action" icon="book" content="View Full Report" onClick={() => onTab("fullReport")} />
              <Button basic className="pi-secondary-action" icon="edit outline" content="Prepare Client Summary" onClick={() => onTab("clientSummary")} />
              <Button primary icon="share" content="Share with Client" onClick={() => onShare(report.clientSummary?.draft?.recipientPartyIds || [])} />
            </div>
          </div>
          {isNarrow ? (
            <div className="pi-tab-select">
              <label htmlFor="pi-tab-select" style={{ position: "absolute", left: -9999 }}>Report section</label>
              <select id="pi-tab-select" value={tab} onChange={(e) => onTab(e.target.value)}>
                {TABS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
            </div>
          ) : (
            <nav className="pi-tabs" role="tablist" aria-label="Report sections">
              {TABS.map(([key, label, icon]) => (
                <button key={key} type="button" role="tab" aria-selected={tab === key} className={`pi-tab ${tab === key ? "active" : ""}`} onClick={() => onTab(key)}>
                  <Icon name={icon} />
                  {label}
                </button>
              ))}
            </nav>
          )}
        </header>

        <div className="pi-body" ref={bodyRef} role="tabpanel">
          {report.status === "partially_completed" && (
            <div className="pi-note" style={{ marginBottom: 16 }}>
              <Icon name="info circle" />
              <div style={{ flex: 1 }}>
                Some research areas weren&apos;t completed{p?.header?.incompleteAreas?.length ? `: ${p.header.incompleteAreas.join(", ")}` : ""}. Nothing was assumed about them.
                {failedSteps(modules).length > 0 && (
                  <Button size="mini" basic style={{ marginLeft: 10 }} loading={resuming} onClick={onResume} content="Resume Research" />
                )}
              </div>
            </div>
          )}
          {!p ? (
            <div className="pi-note neutral">
              <Icon name="info circle" />
              <div>This report view needs the updated Property Intelligence functions. The saved research is unchanged.</div>
            </div>
          ) : (
            <>
              {tab === "overview" && <OverviewTab p={p} onOpenTab={onTab} />}
              {tab === "details" && <PropertyDetailsTab p={p} report={report} onEdit={onEdit} />}
              {tab === "history" && <HistoryPermitsTab p={p} onEdit={onEdit} />}
              {tab === "hoaMetro" && <HoaMetroTab p={p} onEdit={onEdit} />}
              {tab === "risks" && <RisksInsuranceTab p={p} onEdit={onEdit} />}
              {tab === "neighborhood" && <NeighborhoodTab p={p} />}
              {tab === "fullReport" && <PiFullReport p={p} report={report} onEdit={onEdit} />}
              {tab === "clientSummary" && (
                <PiClientSummaryEditor p={p} report={report} clientParties={clientParties} onSave={onSaveSummary} onShare={onShare} />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
